import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  DeleteForever as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Warning as WarningIcon,
  Security as SecurityIcon,
  CloudDownload as CloudDownloadIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { authService } from '../services/authService';
import { User } from '../types';
import { validateRUT, validateEmail } from '../utils/validators';

const AccountSettings: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Estados principales
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados para edición de datos
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    full_name: '',
    email: '',
    rut: ''
  });
  const [editErrors, setEditErrors] = useState<{[key: string]: string}>({});

  // Estados para exportación
  const [exportDialog, setExportDialog] = useState(false);
  const [exportPassword, setExportPassword] = useState('');
  const [showExportPassword, setShowExportPassword] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Estados para eliminación de cuenta
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setEditData({
        full_name: userData.full_name || '',
        email: userData.email,
        rut: userData.rut
      });
    } catch (err) {
      setError('Error al cargar los datos del usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    setEditErrors({});
    setError('');
    setSuccess('');

    // Validaciones
    const errors: {[key: string]: string} = {};
    
    if (!editData.full_name.trim()) {
      errors.full_name = 'El nombre completo es requerido';
    }
    
    if (!editData.email.trim()) {
      errors.email = 'El email es requerido';
    } else if (!validateEmail(editData.email)) {
      errors.email = 'Email inválido';
    }

    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const updatedUser = await authService.updateUser({
        full_name: editData.full_name,
        email: editData.email
      });
      setUser(updatedUser);
      setEditMode(false);
      setSuccess('Datos actualizados correctamente');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al actualizar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    if (!exportPassword) {
      setError('La contraseña es requerida para exportar los datos');
      return;
    }

    setExporting(true);
    setError('');
    
    try {
      const blob = await authService.exportUserData(exportPassword);
      
      // Crear link de descarga
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generar nombre de archivo
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.download = `cuerda_datos_${user?.rut}_${timestamp}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setExportDialog(false);
      setExportPassword('');
      setSuccess('Datos exportados correctamente');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al exportar los datos');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setError('La contraseña es requerida para eliminar la cuenta');
      return;
    }

    setDeleting(true);
    setError('');

    try {
      await authService.deleteAccount(deletePassword);
      // La navegación se manejará automáticamente ya que deleteAccount llama a logout()
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al eliminar la cuenta');
      setDeleting(false);
    }
  };

  const resetEditMode = () => {
    setEditMode(false);
    setEditData({
      full_name: user?.full_name || '',
      email: user?.email || '',
      rut: user?.rut || ''
    });
    setEditErrors({});
  };

  if (loading && !user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Paper
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <AccountCircleIcon sx={{ fontSize: { xs: 48, md: 60 } }} />
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
              Configuración de Cuenta
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Gestiona tus datos personales y configuraciones de seguridad
            </Typography>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Información Personal */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Información Personal
                  </Typography>
                </Box>
                {!editMode && (
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={() => setEditMode(true)}
                  >
                    Editar
                  </Button>
                )}
              </Box>

              {editMode ? (
                <Box component="form" onSubmit={(e) => { e.preventDefault(); handleEditSubmit(); }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nombre Completo"
                        value={editData.full_name}
                        onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                        error={!!editErrors.full_name}
                        helperText={editErrors.full_name}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BadgeIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={editData.email}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        error={!!editErrors.email}
                        helperText={editErrors.email}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="RUT"
                        value={editData.rut}
                        disabled
                        helperText="El RUT no se puede modificar"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BadgeIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box display="flex" gap={2} mt={3}>
                    <Button
                      variant="contained"
                      onClick={handleEditSubmit}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      Guardar Cambios
                    </Button>
                    <Button variant="outlined" onClick={resetEditMode}>
                      Cancelar
                    </Button>
                  </Box>
                </Box>
              ) : (
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <BadgeIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Nombre Completo"
                      secondary={user?.full_name || 'Sin nombre registrado'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={user?.email}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BadgeIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="RUT"
                      secondary={user?.rut}
                    />
                  </ListItem>
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Panel de Acciones */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={3}>
            {/* Exportar Datos */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <CloudDownloadIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Exportar Datos
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Descarga toda tu información personal en formato CSV como respaldo.
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => setExportDialog(true)}
                    color="primary"
                  >
                    Descargar Datos
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Seguridad */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <SecurityIcon color="success" />
                    <Typography variant="h6" fontWeight="bold">
                      Seguridad
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Tu cuenta está protegida con cifrado de extremo a extremo.
                  </Typography>
                  <Chip
                    icon={<LockIcon />}
                    label="Cuenta Segura"
                    color="success"
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Eliminar Cuenta */}
            <Grid item xs={12}>
              <Card sx={{ border: 1, borderColor: 'error.main' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <WarningIcon color="error" />
                    <Typography variant="h6" fontWeight="bold" color="error">
                      Zona Peligrosa
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Eliminar tu cuenta es una acción irreversible. Se eliminarán todos tus datos.
                  </Typography>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialog(true)}
                  >
                    Eliminar Cuenta
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Dialog para Exportar */}
      <Dialog open={exportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CloudDownloadIcon color="primary" />
            Exportar Datos Personales
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Se descargará un archivo CSV con toda tu información personal, incluyendo:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li>Datos personales (nombre, RUT, email)</li>
            <li>Números telefónicos</li>
            <li>Direcciones</li>
            <li>Contactos de emergencia</li>
            <li>Información médica</li>
            <li>Información financiera</li>
            <li>Notas adicionales</li>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Este archivo contiene información sensible. Guárdalo en un lugar seguro.
          </Alert>
          
          <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
            Para acceder a tus datos cifrados, ingresa tu contraseña:
          </Typography>
          
          <TextField
            fullWidth
            type={showExportPassword ? 'text' : 'password'}
            label="Contraseña"
            value={exportPassword}
            onChange={(e) => setExportPassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowExportPassword(!showExportPassword)}
                    edge="end"
                  >
                    {showExportPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setExportDialog(false);
            setExportPassword('');
          }}>
            Cancelar
          </Button>
          <Button
            onClick={handleExportData}
            variant="contained"
            disabled={exporting || !exportPassword}
            startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {exporting ? 'Exportando...' : 'Descargar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Eliminar Cuenta */}
      <Dialog open={deleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="error" />
            Eliminar Cuenta Permanentemente
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold">
              ⚠️ Esta acción NO se puede deshacer
            </Typography>
            <Typography variant="body2">
              Se eliminarán permanentemente todos tus datos, incluyendo tu información de emergencia.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            Para confirmar la eliminación, ingresa tu contraseña:
          </Typography>
          
          <TextField
            fullWidth
            type={showDeletePassword ? 'text' : 'password'}
            label="Contraseña"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowDeletePassword(!showDeletePassword)}
                    edge="end"
                  >
                    {showDeletePassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialog(false);
            setDeletePassword('');
          }}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={deleting || !deletePassword}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Eliminando...' : 'Eliminar Cuenta'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountSettings; 